import { Formik } from "formik";
import ErrorBox from "lib/components/common/error-box";
import SimpleCard from "lib/components/common/simple-card";
import SimpleTextbox from "lib/components/common/simple-textbox";
import { REQUIRED_FIELD_VALIDATION_SCHEMA } from "lib/constants/validation-constants";
import { FC, useRef } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import { toast } from "sonner";

interface PackageDetailsProps {
  error?: string;
  isLoading?: boolean;
  packageData: Package;
  updatePackage: (packageData: Package) => void;
  onCancelClick?: () => void;
  addPackage?: (packageData: Package) => void;
  deletePackage?: (packageId: string) => void;
}

const PackageDetails: FC<PackageDetailsProps> = ({
  error,
  isLoading,
  packageData,
  onCancelClick,
  updatePackage,
  addPackage,
  deletePackage,
}) => {
  const formikRef = useRef(null);

  return (
    <SimpleCard
      title="Package Details"
      isLoading={isLoading}
      action={
        <Button
          type="button"
          variant="outline-primary"
          className="px-4"
          onClick={() => {
            formikRef.current?.submitForm();
          }}
        >
          Save Package
        </Button>
      }
      isShowBackButton
      onBackButtonClick={onCancelClick}
      error={error}
    >
      <Row>
        <Col sm="12">
          <Formik
            initialValues={
              {
                ...packageData,
                validity: packageData.validity || 0,
                credits: packageData.credits || 0,
                price: packageData.price || 0,
              } as Package
            }
            onSubmit={(values: Package, { setSubmitting }) => {
              if (!values.name) {
                toast.error("Please enter package name");
                return;
              }

              if (!values.description) {
                toast.error("Please enter package description");
                return;
              }

              if (values.credits < 0) {
                toast.error("Credits cannot be negative");
                return;
              }

              if (values.price < 0) {
                toast.error("Price cannot be negative");
                return;
              }

              const formattedValues = {
                ...values,
                validity: values.validity === "" ? undefined : values.validity,
              };

              if (!packageData.id) {
                addPackage(formattedValues);
              } else {
                updatePackage(formattedValues);
              }
              setSubmitting(false);
            }}
            innerRef={formikRef}
          >
            {({ values, handleChange, errors, setValues }) => (
              <Form>
                <SimpleTextbox
                  label="Package Name"
                  type="text"
                  name="name"
                  value={values.name}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Package name is required"
                  )}
                />

                <SimpleTextbox
                  label="Description"
                  as="textarea"
                  name="description"
                  value={values.description}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Package description is required"
                  )}
                />

                <SimpleTextbox
                  label="Credits"
                  type="number"
                  name="credits"
                  value={values.credits}
                  className="form-control"
                  onChange={handleChange}
                  min="0"
                  required
                />

                <SimpleTextbox
                  label="Price (£)"
                  type="number"
                  name="price"
                  value={values.price}
                  className="form-control"
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />

                <SimpleTextbox
                  label="Validity (Optional)"
                  type="number"
                  name="validity"
                  value={values.validity || ""}
                  className="form-control"
                  onChange={handleChange}
                />
              </Form>
            )}
          </Formik>
        </Col>
        {packageData?.id && (
        <Col sm="12">
          <hr />
          <Button
            type="button"
            variant="outline-danger"
            className="px-4 text-danger mr-1"
            onClick={deletePackage?.bind(null, packageData.id)}>
              Delete Package
          </Button>
        </Col>
        )}
      </Row>
    </SimpleCard>
  );
};

export default PackageDetails;